import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// context
import { userContext } from "../../../context/user-provider/UserProvider";
import { errorContext } from "../../../context/error-provider/ErrorProvider";

// consts
import { INIT_USER_FORM_DATA } from "./UsersScreen.consts";

// types
import { ChangeUserRoleType } from "../../../context/user-provider/UserProvider.types";

export function useUsersForm() {
  const { error, success } = useContext(errorContext);
  const { users, updateUserById } = useContext(userContext);
  const [userFormData, setUserFormData] =
    useState<ChangeUserRoleType>(INIT_USER_FORM_DATA);

  const handleChangeUserData = useCallback(
    (newValue: unknown, type: "email_id" | "role") => {
      // TODO  Fix the react select types and remove this "as"
      const option = newValue as { label: string; value: string };

      setUserFormData((prev) => ({
        ...prev,
        [type]: option.value,
      }));
    },
    [setUserFormData]
  );

  const onSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      try {
        await updateUserById(userFormData.email_id, userFormData.role);
        success("The role has been successfully changed.");
      } catch (e) {
        error(e);
      }
    },
    [userFormData, updateUserById, success, error]
  );

  const usersEmailsOptions = useMemo(() => {
    if (users) {
      const emailOptions = users.map((user) => ({
        label: user.email || user.username,
        value: user.id,
      }));

      return emailOptions;
    }

    return [{ label: "", value: "" }];
  }, [users]);

  const profileRoleOptions = useMemo(
    () => [
      { label: "User", value: "user" },
      { label: "Admin", value: "admin" },
      { label: "Moderator", value: "moderator" },
    ],
    []
  );

  return {
    userFormData,
    usersEmailsOptions,
    profileRoleOptions,
    handleChangeUserData,
    onSubmit,
  };
}

export function useUsersFetch() {
  const { error } = useContext(errorContext);
  const { getUsers } = useContext(userContext);

  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const usersFetch = async () => {
    try {
      setIsUsersLoading(true);

      await getUsers();
    } catch (err) {
      error(err);
    } finally {
      setIsUsersLoading(false);
    }
  };

  useEffect(() => {
    usersFetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isUsersLoading };
}
